import React, { useEffect, useState } from "react";
import { Skeleton, Empty, Card, Tag } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import drEasyApi from "../../Api/Customapi";
import { getImg } from "../../Api/getImg";

const ReportTab = ({ patient }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        if (!patient?.id) return;

        const fetchReport = async () => {
            try {
                setLoading(true);
                const res = await drEasyApi.get("/api/Reports/GetUserReport", {
                    params: { UserId: patient.id },
                });
                if (res.status === 204) {
                    setMessage("This patient doesn't have any completed bookings yet.");
                    return;
                }
                setReport(res.data);
            } catch (err) {
                if (err.response?.status === 404) {
                    setMessage("Patient or doctor not found.");
                } else if (err.response?.status === 204) {
                    setMessage("This patient doesn't have any completed bookings yet.");
                } else if (err.response?.status === 403) {
                    setMessage("You are not authorized to view this report.");
                } else {
                    setMessage("Failed to load report data.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [patient?.id]);

    if (loading) {
        return (
            <div className="space-y-4 p-4">
                <Skeleton active paragraph={{ rows: 3 }} />
                <Skeleton active paragraph={{ rows: 3 }} />
            </div>
        );
    }

    if (message || !report) {
        return (
            <div className="py-12">
                <Empty
                    image={<FileTextOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />}
                    description={<span className="text-gray-500">{message || "No report available."}</span>}
                />
            </div>
        );
    }

    return (
        <div className="p-4 space-y-6">
            {/* Report body */}
            {report.body && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-line">{report.body}</p>
                </div>
            )}

            {/* Sections */}
            {report.reportSections?.length > 0 ? (
                <div className="space-y-4">
                    {report.reportSections.map((section) => (
                        <Card
                            key={section.id}
                            className="shadow-sm border border-gray-200 rounded-xl overflow-hidden"
                            title={
                                <div className="flex items-center justify-between">
                                    <span className="font-semibold text-gray-800">{section.title}</span>
                                    <span className="text-xs text-gray-400 font-normal">
                                        {new Date(section.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                            }
                            headStyle={{ backgroundColor: "#f0fafb", borderBottom: "1px solid #e0f0f2" }}
                        >
                            {/* Body text */}
                            {section.body && (
                                <p className="text-gray-700 whitespace-pre-line mb-4">{section.body}</p>
                            )}

                            {/* Photos */}
                            {section.photoIds?.length > 0 && (
                                <div className="flex flex-wrap gap-3 mt-2">
                                    {section.photoIds.map((photoId) => (
                                        <img
                                            key={photoId}
                                            src={getImg(photoId)}
                                            alt="Report attachment"
                                            className="w-24 h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                            onClick={() => window.open(getImg(photoId), "_blank")}
                                        />
                                    ))}
                                </div>
                            )}
                        </Card>
                    ))}
                </div>
            ) : (
                <Empty description="No report sections found." />
            )}
        </div>
    );
};

export default ReportTab;